/**
 * Promotion countdown in the announcement bar.
 *
 * The element ships with the remaining time already rendered by Liquid, so
 * there is no flash of an empty clock; this only keeps it ticking and decides
 * what happens at zero. The deadline travels as an absolute epoch
 * (`data-ends`, seconds), never as a formatted date, so the store's timezone
 * and the visitor's do not have to agree.
 *
 * Shopify serves the header from a full-page cache, so the server-rendered
 * digits can be a few minutes stale — including "still running" for a promo
 * that has just ended. The first tick fires immediately and corrects both.
 *
 * At zero the bar either disappears or hands over to the standing announcement
 * text, per the section's "When the countdown ends" setting. In the theme
 * editor (`data-ag-editing`) it does neither: a merchant setting up a past or
 * finished sale still has to be able to see and style the bar.
 */
class AgCountdown extends HTMLElement {
  #timer = 0;

  connectedCallback() {
    this.ends = Number(this.dataset.ends || 0) * 1000;
    if (!this.ends) return;
    this.#tick();
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
  }

  #tick() {
    const left = Math.floor((this.ends - Date.now()) / 1000);

    if (left <= 0) {
      this.textContent = '00:00:00';
      this.#expire();
      return;
    }

    this.textContent = AgCountdown.format(left);

    // Re-armed against the deadline rather than on a fixed 1000ms interval: a
    // backgrounded tab is throttled, and the drift would show up as skipped
    // seconds. Wait exactly until the displayed second is due to change.
    const nextSecond = ((this.ends - Date.now()) % 1000) + 1;
    this.#timer = setTimeout(() => this.#tick(), Math.max(50, nextSecond));
  }

  static format(seconds) {
    const pad = (n) => String(n).padStart(2, '0');
    const days = Math.floor(seconds / 86400);
    const clock = `${pad(Math.floor((seconds % 86400) / 3600))}:${pad(Math.floor((seconds % 3600) / 60))}:${pad(seconds % 60)}`;
    return days > 0 ? `${days}d ${clock}` : clock;
  }

  #expire() {
    const bar = this.closest('.ag-announcement');
    if (!bar || bar.hasAttribute('data-ag-editing')) return;

    const fallback = bar.querySelector('[data-ag-promo-fallback]');
    if (this.dataset.expired === 'message' && fallback) {
      this.closest('.ag-promo')?.remove();
      bar.classList.remove('ag-announcement--promo');
      fallback.hidden = false;
      return;
    }

    bar.hidden = true;
  }
}

if (!customElements.get('ag-countdown')) customElements.define('ag-countdown', AgCountdown);
